/**
 * Spring(onegrid-spring) 연동 도우미
 *  - 조회: GridData(컬럼형 JSON) → Dataset.loadColumnar (행 객체를 만들지 않아 바인딩이 가장 빠름)
 *  - 저장: Dataset.getChanges() → POST → 성공 시 commit()
 */
import { Dataset, mapServerErrors } from './dataset.js';
/** RequestInit.headers 를 객체로 (1.2.0: Headers 인스턴스·[이름, 값] 배열도 — 예전에는 펼치기(...)로 Headers 가 빠졌음) */
function hdrs(h) {
    if (!h)
        return {};
    if (typeof Headers !== 'undefined' && h instanceof Headers)
        return Object.fromEntries(h.entries());
    if (Array.isArray(h))
        return Object.fromEntries(h);
    return h;
}
export async function fetchColumnar(ds, url, init) {
    const t0 = performance.now();
    const res = await fetch(url, { ...init, headers: { Accept: 'application/json', ...hdrs(init?.headers) } });
    if (!res.ok)
        throw new Error(`조회 실패 (${res.status}) ${await res.text().catch(() => '')}`);
    const text = await res.text();
    const t1 = performance.now();
    const data = JSON.parse(text);
    const t2 = performance.now();
    ds.loadColumnar(data);
    const t3 = performance.now();
    const sm = res.headers.get('X-Query-Ms');
    return { fetchMs: t1 - t0, parseMs: t2 - t1, bindMs: t3 - t2, rows: data.n, bytes: text.length, serverMs: sm == null ? undefined : Number(sm), source: res.headers.get('X-Source') ?? undefined };
}
/** 충돌 한 건의 해결 동작 이름 (화면 단추 글자·기록용, 순수) */
export function conflictAction(cf, choice) {
    if (choice === 'server')
        return cf.server ? 'takeServer' : 'dropRow';
    return cf.server ? 'overwrite' : cf.section === 'deleted' ? 'confirmDelete' : 'reAdd';
}
/**
 * 저장 충돌 한 건을 Dataset 에 반영 (grid.resolveConflicts 가 행마다 부름, 화면 없이도 사용 가능).
 *  - 덮어쓰기: 버전만 서버 것으로 → 다시 저장하면 내 값으로 덮어씀
 *  - 다시 추가(서버에서 지워진 행): 같은 키로 INSERT 상태 (reAddKey:'new' 면 키 없는 새 행)
 *  - 삭제 확정·서버에서 지워진 행의 서버 값 받기: 화면에서 제거
 *  - 서버 값 받기: 행을 서버 값으로 (NORMAL)
 */
export function applyConflict(ds, cf, choice, opts = {}) {
    const vc = opts.versionCol ?? 'ver', kc = ds.keyCol;
    const act = conflictAction(cf, choice);
    const drop = () => { if (kc && cf.key != null)
        ds.applyTransaction({ remove: [cf.key] });
    else if (cf.row >= 0)
        ds.revertRow(cf.row); };
    if (act === 'overwrite')
        ds.setBase(cf.row, vc, cf.server[vc] ?? null);
    else if (act === 'reAdd') {
        if (opts.reAddKey === 'new' || cf.row < 0) {
            const vals = { ...cf.mine };
            for (const k of ds.keyCols() ?? [])
                vals[k] = null;
            vals[vc] = null;
            ds.batch(() => { ds.addRow(vals); drop(); });
        }
        else
            ds.batch(() => { if (ds.hasColumn(vc))
                ds.setBase(cf.row, vc, null); ds.markInserted(cf.row); });
    }
    else if (act === 'takeServer') {
        if (kc)
            ds.applyTransaction({ update: [cf.server] });
    }
    else
        drop();
}
/**
 * 변경분 저장. 서버가 2xx 를 주면 commit() 해서 행 상태를 NORMAL 로 만든다.
 * opts.grid 를 주면 먼저 grid.validateAll() (필수·검증·비동기 검증 대기) — 오류가 있으면 보내지 않고 status 0, errors 로 돌려준다.
 */
export async function saveChanges(ds, url, init, opts) {
    if (opts?.grid) {
        const errs = await opts.grid.validateAll();
        if (errs.length)
            return { ok: false, status: 0, body: null, rows: { inserted: [], updated: [], deleted: [] }, errors: errs, conflicts: [] };
    }
    const changes = ds.getChanges();
    const rows = ds.lastChangeRows;
    const res = await fetch(url, {
        method: 'POST',
        ...init,
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...hdrs(init?.headers) },
        body: JSON.stringify(changes),
    });
    const body = await res.json().catch(() => null);
    if (res.ok)
        ds.commit();
    const errs = Array.isArray(body?.errors) ? mapServerErrors(body.errors, rows) : [];
    const conflicts = Array.isArray(body?.conflicts)
        ? body.conflicts.map((c) => { const row = rows[c.section]?.[c.index] ?? -1; return { row, section: c.section, index: c.index, key: c.key, server: c.server ?? null, mine: row >= 0 ? ds.getRow(row) : {} }; })
        : [];
    return { ok: res.ok, status: res.status, body, rows, errors: errs, conflicts };
}
/**
 * 여러 Dataset 을 한 요청·한 트랜잭션으로 저장 (마스터 + 디테일 등).
 *   본문: { orders: ds1.getChanges(), details: ds2.getChanges(), … }
 *   성공(2xx)이면 모두 commit, 실패면 아무것도 commit 하지 않음 (서버도 전부 롤백)
 *   오류 { errors: [{ dataset, section, index, field, message }] } → Dataset 별로 행·셀에, 그리드가 있으면 showServerErrors
 * 그리드를 주면 먼저 전부 validateAll() — 오류가 있으면 보내지 않고 status 0.
 */
export async function saveTransaction(url, sets, opts = {}) {
    var _a, _b;
    const ent = Object.entries(sets).map(([name, v]) => [name, v instanceof Dataset ? { ds: v } : v]);
    const res0 = { ok: false, status: 0, body: null, rows: {}, errors: {}, conflicts: {}, failed: null };
    if (opts.validate !== false) {
        for (const [name, s] of ent) {
            if (!s.grid)
                continue;
            const errs = await s.grid.validateAll();
            if (errs.length) {
                res0.errors[name] = errs;
                res0.failed ?? (res0.failed = name);
            }
        }
        if (res0.failed)
            return res0;
    }
    const body = {};
    for (const [name, s] of ent) {
        body[name] = s.ds.getChanges();
        res0.rows[name] = s.ds.lastChangeRows;
    }
    const res = await fetch(url, {
        method: 'POST', ...opts.init,
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...hdrs(opts.init?.headers) },
        body: JSON.stringify(body),
    });
    const rb = await res.json().catch(() => null);
    const out = { ...res0, ok: res.ok, status: res.status, body: rb };
    if (res.ok) {
        for (const [, s] of ent)
            s.ds.commit();
        return out;
    }
    const byName = new Map(ent);
    for (const e of (Array.isArray(rb?.errors) ? rb.errors : [])) {
        const name = e.dataset && byName.has(e.dataset) ? e.dataset : ent[0]?.[0];
        if (!name)
            continue;
        ((_a = out.errors)[name] ?? (_a[name] = [])).push(...mapServerErrors([e], out.rows[name]));
        out.failed ?? (out.failed = name);
    }
    for (const c of (Array.isArray(rb?.conflicts) ? rb.conflicts : [])) {
        const name = c.dataset && byName.has(c.dataset) ? c.dataset : ent[0]?.[0];
        if (!name)
            continue;
        const ds = byName.get(name).ds, row = out.rows[name][c.section]?.[c.index] ?? -1;
        ((_b = out.conflicts)[name] ?? (_b[name] = [])).push({ row, section: c.section, index: c.index, key: c.key, server: c.server ?? null, mine: row >= 0 ? ds.getRow(row) : {} });
        out.failed ?? (out.failed = name);
    }
    for (const [name, s] of ent) {
        if (!s.grid)
            continue;
        if (out.errors[name]?.length)
            s.grid.showServerErrors(out.errors[name], out.rows[name]);
        else
            s.grid.clearErrors();
    }
    return out;
}
/** Spring CellNoteController(`GET/PUT/DELETE {baseUrl}/{screen}`) 셀 메모 저장소 → grid 옵션 `noteStore`
 *  springNoteStore('/api/notes', 'OrderList', 'demo') 또는 springNoteStore('/api/notes', { screen: 'OrderList', scope: 'patient', user, role: 'nurse' }) */
export function springNoteStore(baseUrl, screen, user, init) {
    const o = typeof screen === 'string' ? { screen, user, init } : { user, init, ...screen };
    const url = `${baseUrl.replace(/\/$/, '')}/${encodeURIComponent(o.scope || o.screen)}`;
    const alias = o.colAlias ?? {}, back = new Map(Object.entries(alias).map(([k, v]) => [v, k]));
    const headers = (extra = {}) => ({ ...(o.user ? { 'X-User': o.user } : {}), ...(o.role ? { 'X-Role': encodeURIComponent(o.role) } : {}), ...hdrs(o.init?.headers), ...extra });
    const ok = async (res) => { if (!res.ok) {
        const b = await res.json().catch(() => null);
        throw new Error(b?.message ?? `HTTP ${res.status}`);
    } };
    const init0 = o.init;
    return {
        async load() { const res = await fetch(url, { ...init0, headers: headers({ Accept: 'application/json' }) }); await ok(res); return (await res.json()).map(n => (back.has(n.col) ? { ...n, col: back.get(n.col) } : n)); },
        async save(n) { await ok(await fetch(url, { ...init0, method: 'PUT', headers: headers({ 'Content-Type': 'application/json' }), body: JSON.stringify({ rowKey: n.rowKey, col: alias[n.col] ?? n.col, note: n.note }) })); },
        async remove(rowKey, col) { await ok(await fetch(`${url}?${new URLSearchParams({ rowKey, col: alias[col] ?? col })}`, { ...init0, method: 'DELETE', headers: headers() })); },
    };
}
/** Spring LayoutController(`GET/PUT/DELETE {baseUrl}/{screen}`, 헤더 X-User) 레이아웃 저장소.
 *  grid 옵션 `layoutKey` + `layoutStore: springLayoutStore('/api/layouts', userId)` */
export function springLayoutStore(baseUrl, user, init) {
    const url = (key) => `${baseUrl.replace(/\/$/, '')}/${encodeURIComponent(key)}`;
    const headers = (extra = {}) => ({ ...(user ? { 'X-User': user } : {}), ...hdrs(init?.headers), ...extra });
    return {
        async load(key) {
            const res = await fetch(url(key), { ...init, headers: headers({ Accept: 'application/json' }) });
            if (res.status === 404 || res.status === 204)
                return null;
            if (!res.ok)
                throw new Error(`레이아웃 조회 실패 (${res.status})`);
            return (await res.json());
        },
        async save(key, layout) {
            // keepalive: 페이지를 떠나는 중(pagehide)에도 요청이 끝까지 감 (본문 64KB 이하)
            const res = await fetch(url(key), { keepalive: true, ...init, method: 'PUT', headers: headers({ 'Content-Type': 'application/json' }), body: JSON.stringify(layout) });
            if (!res.ok)
                throw new Error(`레이아웃 저장 실패 (${res.status})`);
        },
        async remove(key) { await fetch(url(key), { ...init, method: 'DELETE', headers: headers() }); },
    };
}
